import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  HandCoins,
  Landmark,
  Lock,
  TriangleAlert,
  Wallet,
} from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import { formatLbp, formatMoment, formatUsd } from '@/lib/money'
import { DrawerStatement, type DrawerStatementData } from '@/pages/cash/DrawerStatement'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, EmptyState, Modal, Select, Tabs } from '@/components/ui/primitives'
import { useSession } from '@/stores/session'
import { Permission } from '@/lib/permissions'
import { ReverseButton, ReverseDialog, ReversedBadge } from '@/components/ReverseDialog'
import { t } from '@/i18n'
import { ListFilters, Pager, NO_FILTERS, type ListFilterState } from '@/components/ListControls'
import { usePagedList } from '@/lib/paging'

interface ExpectedCash {
  openingFloatUsd: number
  openingFloatLbp: number
  cashSalesUsd: number
  cashSalesLbp: number
  changeGivenUsd: number
  changeGivenLbp: number
  refundsUsd: number
  refundsLbp: number
  payInsUsd: number
  payInsLbp: number
  payOutsUsd: number
  payOutsLbp: number
  expectedUsd: number
  expectedLbp: number
}

type TabValue = 'drawer' | 'safe' | 'close'

/** One till on the picker: what it is, whether it is open, and what it should be holding. */
interface DrawerOption {
  registerId: number
  registerName: string
  shiftOpen: boolean
  openedByDisplayName: string | null
  expectedUsd: number | null
  expectedLbp: number | null
}

export function CashPage() {
  const queryClient = useQueryClient()
  const can = useSession((state) => state.can)
  const [tab, setTab] = useState<TabValue>('drawer')
  const [closed, setClosed] = useState<CloseResult | null>(null)
  /** Null until the user picks; the first till stands in until then. */
  const [pickedRegisterId, setPickedRegisterId] = useState<number | null>(null)

  const registers = useQuery({
    queryKey: ['registers'],
    queryFn: () =>
      apiClient.get<{ id: number; name: string; currentShiftId: number | null }[]>('/api/registers'),
  })

  // Every till on the branch with what each is holding, so an owner running one drawer per
  // cashier can see them side by side. Only they can ask for it — a cashier sees their own.
  const drawers = useQuery({
    queryKey: ['registers', 'all'],
    queryFn: () =>
      apiClient.get<
        (DrawerOption & { branchName: string; shiftId: number | null })[]
      >('/api/registers/all'),
    enabled: can(Permission.ManageSafe),
  })

  const options: DrawerOption[] =
    drawers.data ??
    (registers.data ?? []).map((register) => ({
      registerId: register.id,
      registerName: register.name,
      shiftOpen: register.currentShiftId !== null,
      openedByDisplayName: null,
      expectedUsd: null,
      expectedLbp: null,
    }))

  // Falls back rather than syncing through an effect, so there is never a render where a till
  // is loaded but none is selected.
  const registerId = pickedRegisterId ?? options[0]?.registerId ?? null

  const shift = useQuery({
    queryKey: ['shift', registerId],
    queryFn: () => apiClient.get<{ id: number } | null>(`/api/shifts/current?registerId=${registerId}`),
    enabled: registerId !== null,
  })
  const shiftId = shift.data?.id ?? null

  const statement = useQuery({
    queryKey: ['shift-statement', shiftId],
    queryFn: () => apiClient.get<DrawerStatementData>(`/api/shifts/${shiftId}/statement`),
    enabled: shiftId !== null,
  })

  const expected = useQuery({
    queryKey: ['shift-expected', shiftId],
    queryFn: () => apiClient.get<ExpectedCash>(`/api/shifts/${shiftId}/expected`),
    enabled: shiftId !== null,
  })

  const safe = useQuery({
    queryKey: ['safe'],
    queryFn: () => apiClient.get<{ usd: number; lbp: number }>('/api/cash/safe'),
    enabled: can(Permission.ManageSafe),
  })

  // Needed to combine the two counted currencies into a single net position.
  const rate = useQuery({
    queryKey: ['exchange-rate'],
    queryFn: () => apiClient.get<{ lbpPerUsd: number }>('/api/exchange-rate'),
  })

  // The closing summary outlives the shift it describes. It is held here rather than inside
  // CloseSection because closing makes the shift query return nothing, which unmounts every
  // section below — and the count a cashier just signed off would vanish with them.
  if (closed) {
    return (
      <div className="pb-page mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <ClosedSummary result={closed} onDone={() => setClosed(null)} />
      </div>
    )
  }

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['shift-expected'] })
    await queryClient.invalidateQueries({ queryKey: ['shift-statement'] })
    await queryClient.invalidateQueries({ queryKey: ['safe'] })
    // The picker shows each till's own figure, so it goes stale the moment cash moves.
    await queryClient.invalidateQueries({ queryKey: ['registers', 'all'] })
  }

  // Same reasoning as the Drawers screen: a refused request must not render as a shop with no
  // tills. Without this the Cash page just shows nothing and the owner has no way to tell a
  // broken endpoint from an empty branch.
  const failedLoad = drawers.error ?? registers.error
  const loadError = failedLoad
    ? failedLoad instanceof ApiError
      ? (failedLoad.detail ?? failedLoad.message)
      : 'the server did not answer'
    : null

  const selectedName =
    options.length > 1
      ? (options.find((option) => option.registerId === registerId)?.registerName ?? null)
      : null

  const picker = options.length > 1 && (
    <div className="mb-5">
      <p className="mb-2 text-sm text-[var(--text-secondary)]">
        {/* Named, not implied. Every action below lands on this till and no other. */}
        Which till
      </p>
      <ul className="grid gap-2 sm:grid-cols-2">
        {options.map((option) => {
          const isSelected = option.registerId === registerId
          return (
            <li key={option.registerId}>
              <button
                type="button"
                onClick={() => setPickedRegisterId(option.registerId)}
                aria-pressed={isSelected}
                className={`surface-raised w-full rounded-[var(--radius-card)] border p-3 text-left ${
                  isSelected ? 'border-[var(--accent)] ring-1 ring-[var(--accent)]' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium">{option.registerName}</span>
                  <Badge tone={option.shiftOpen ? 'accent' : 'neutral'}>
                    {option.shiftOpen ? 'Open' : 'Closed'}
                  </Badge>
                </div>
                {option.expectedUsd !== null && (
                  <p className="tabular mt-1 text-lg font-semibold">
                    {formatUsd(option.expectedUsd)}
                    {option.expectedLbp !== null && option.expectedLbp !== 0 && (
                      <span className="ml-2 text-xs font-normal text-[var(--text-secondary)]">
                        {formatLbp(option.expectedLbp)}
                      </span>
                    )}
                  </p>
                )}
                {option.openedByDisplayName && (
                  <p className="truncate text-xs text-[var(--text-secondary)]">
                    Opened by {option.openedByDisplayName}
                  </p>
                )}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )

  // The safe is not part of any drawer, so it stays reachable whether or not a till is open.
  // Reaching it used to mean opening a drawer first, which is backwards: a shop counts the safe
  // and moves money in and out of it precisely when the tills are shut.
  const hasShift = shiftId !== null
  const canSafe = can(Permission.ManageSafe)

  const tabs = [
    ...(hasShift ? [{ value: 'drawer' as const, label: t('Drawer') }] : []),
    ...(canSafe ? [{ value: 'safe' as const, label: t('Safe') }] : []),
    ...(hasShift && can(Permission.CloseShift)
      ? [{ value: 'close' as const, label: t('Close shift') }]
      : []),
  ]

  // Falls back rather than syncing through an effect, so closing the last till cannot leave the
  // screen pointing at a tab that no longer exists.
  const activeTab = tabs.some((entry) => entry.value === tab) ? tab : tabs[0]?.value

  return (
    <div className="pb-page mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
      <header className="mb-5">
        <h1 className="text-xl font-semibold">{t('Cash')}</h1>
        <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
          {t('Dollars and lira are counted separately, because they are separate piles of notes.')}
        </p>
      </header>

      {loadError && (
        <p role="alert" className="mb-5 flex items-start gap-2 rounded-lg bg-danger-500/10 px-3 py-3 text-sm text-danger-500">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {t('Could not load the tills — {error}. If this store was updated recently, apply its pending migrations from the platform panel.', { error: loadError })}
          </span>
        </p>
      )}

      {picker}

      {(expected.data || safe.data) && (
        <div className="surface-raised mb-5 grid gap-4 rounded-[var(--radius-card)] border p-4 sm:grid-cols-2">
          {expected.data && (
            <div>
              <p className="text-sm text-[var(--text-secondary)]">
                {/* Named once there is more than one, so a figure can never be read against the
                    wrong till. */}
                {selectedName
                  ? t('{drawer} should hold', { drawer: selectedName })
                  : t('Drawer should hold')}
              </p>
              <p className="tabular mt-1 text-2xl font-semibold">
                {formatUsd(expected.data.expectedUsd)}
              </p>
              <p className="tabular text-sm text-[var(--text-secondary)]">
                {formatLbp(expected.data.expectedLbp)}
              </p>
            </div>
          )}
          {safe.data && (
            <div>
              <p className="text-sm text-[var(--text-secondary)]">{t('Safe holds')}</p>
              <p className="tabular mt-1 text-2xl font-semibold">{formatUsd(safe.data.usd)}</p>
              <p className="tabular text-sm text-[var(--text-secondary)]">
                {formatLbp(safe.data.lbp)}
              </p>
            </div>
          )}
        </div>
      )}

      {tabs.length > 1 && (
        <div className="mb-5">
          <Tabs tabs={tabs} value={activeTab!} onChange={setTab} />
        </div>
      )}

      {!hasShift && activeTab !== 'safe' && (
        <EmptyState
          icon={Wallet}
          title={t('No shift is open')}
          description={t('Open a till from the checkout screen before moving cash through a drawer.')}
        />
      )}

      {activeTab === 'drawer' && hasShift && (
        <DrawerSection shiftId={shiftId} statement={statement.data} onDone={refresh} />
      )}
      {activeTab === 'safe' && canSafe && <SafeSection onDone={refresh} />}
      {activeTab === 'close' && hasShift && can(Permission.CloseShift) && (
        <CloseSection
          shiftId={shiftId}
          expected={expected.data}
          statement={statement.data}
          rate={rate.data?.lbpPerUsd ?? 0}
          onDone={refresh}
          onClosed={setClosed}
        />
      )}
    </div>
  )
}

/* -------------------------------------------------------------- closed shift */

/**
 * What the count came to, shown after the shift it belongs to has already gone.
 *
 * Rendered from `CashPage` rather than from the closing form, because closing the shift is
 * precisely what removes that form from the screen.
 */
function ClosedSummary({ result, onDone }: { result: CloseResult; onDone: () => void }) {
  const over = result.varianceTotalUsd >= 0

  return (
    <div className="surface-raised rounded-[var(--radius-card)] border p-5 text-center">
      <p className="text-sm text-[var(--text-secondary)]">{t('Shift closed')}</p>

      {/* The net position leads, because that is the question — is any money missing. */}
      <p
        className={`tabular mt-2 text-3xl font-semibold ${
          result.balancesOverall ? 'text-success-500' : over ? 'text-warn-500' : 'text-danger-500'
        }`}
      >
        {result.balancesOverall
          ? 'Balanced'
          : `${formatUsd(Math.abs(result.varianceTotalUsd))} ${over ? 'over' : 'short'}`}
      </p>

      {/* The per-currency detail still shows, so a swap between the piles is visible. */}
      {(result.varianceUsd !== 0 || result.varianceLbp !== 0) && (
        <p className="tabular mt-2 text-sm text-[var(--text-secondary)]">
          {result.varianceUsd !== 0 &&
            `${formatUsd(Math.abs(result.varianceUsd))} ${result.varianceUsd > 0 ? 'over' : 'short'} in dollars`}
          {result.varianceUsd !== 0 && result.varianceLbp !== 0 && ' · '}
          {result.varianceLbp !== 0 &&
            `${formatLbp(Math.abs(result.varianceLbp))} ${result.varianceLbp > 0 ? 'over' : 'short'} in lira`}
        </p>
      )}

      {result.balancesOverall && (result.varianceUsd !== 0 || result.varianceLbp !== 0) && (
        <p className="mt-3 text-xs text-[var(--text-secondary)]">
          {t('The two cancel out at today\'s rate — change was given from the other currency.')}
        </p>
      )}

      <p className="mt-4 text-xs text-[var(--text-secondary)]">
        {t('The till is closed. Open a new one from the checkout screen to keep selling.')}
      </p>

      <Button variant="secondary" className="mt-4" onClick={onDone}>
        {t('Done')}
      </Button>
    </div>
  )
}

/* ------------------------------------------------------------------- drawer */

function DrawerSection({
  shiftId,
  statement,
  onDone,
}: {
  shiftId: number
  statement: DrawerStatementData | undefined
  onDone: () => Promise<void>
}) {
  const queryClient = useQueryClient()
  const [isOpen, setOpen] = useState(false)
  const [type, setType] = useState('PayIn')
  const [usd, setUsd] = useState('')
  const [lbp, setLbp] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const movements = useQuery({
    queryKey: ['drawer', shiftId],
    queryFn: () => apiClient.get<DrawerRow[]>(`/api/cash/drawer/${shiftId}`),
  })

  /** The movement being undone. */
  const [reversingMovement, setReversingMovement] = useState<DrawerRow | null>(null)

  const record = useMutation({
    mutationFn: () =>
      apiClient.post('/api/cash/drawer', {
        shiftId,
        type,
        amountUsd: Number(usd || 0),
        amountLbp: Number(lbp || 0),
        reason: reason.trim(),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['drawer', shiftId] })
      await onDone()
      setOpen(false)
      setUsd('')
      setLbp('')
      setReason('')
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not record it.'),
  })

  return (
    <div>
      {statement && (
        <div className="mb-4">
          <DrawerStatement statement={statement} />
        </div>
      )}

      <div className="mb-3 flex flex-wrap gap-2">
        {/* First, and the primary button: a till opens empty, so this is how the float gets
            into it. It was reachable only from a dropdown inside the pay-in dialog, which is
            no place for the commonest thing anyone does to a drawer at the start of a day. */}
        <Button onClick={() => { setType('WithdrawFromSafe'); setOpen(true); setError(null) }}>
          <Landmark className="h-4 w-4" />
          {t('Draw from safe')}
        </Button>
        <Button variant="secondary" onClick={() => { setType('DropToSafe'); setOpen(true); setError(null) }}>
          <Landmark className="h-4 w-4" />
          {t('Drop to safe')}
        </Button>
        <Button variant="secondary" onClick={() => { setType('PayIn'); setOpen(true); setError(null) }}>
          <ArrowDownToLine className="h-4 w-4" />
          {t('Pay in')}
        </Button>
        <Button variant="secondary" onClick={() => { setType('PayOut'); setOpen(true); setError(null) }}>
          <ArrowUpFromLine className="h-4 w-4" />
          {t('Pay out')}
        </Button>
      </div>

      {movements.data?.length === 0 ? (
        <EmptyState
          icon={Wallet}
          title={t('No cash movements')}
          description={t('Anything that moves cash other than a sale shows here.')}
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {movements.data?.map((movement) => (
            <li
              key={movement.id}
              className="surface-raised flex items-center gap-3 rounded-[var(--radius-card)] border p-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p
                    className={`text-sm font-medium ${
                      movement.reversedAtUtc ? 'text-[var(--text-secondary)] line-through' : ''
                    }`}
                  >
                    {movement.type}
                  </p>
                  {movement.reversedAtUtc && <ReversedBadge reason={movement.reversalReason} />}
                  {/* Both rows stay in the expected-cash figure and net to nothing, so the
                      contra is labelled rather than hidden. */}
                  {movement.reversalOfId != null && <Badge tone="accent">{t('Reversal')}</Badge>}
                </div>
                {/* The clock, not just the day. Reconciling a drawer against a paper slip
                    means matching the moment it happened, and two pay-outs of the same amount
                    on one shift are otherwise indistinguishable. */}
                <p className="truncate text-xs text-[var(--text-secondary)]">
                  {formatMoment(movement.occurredAtUtc)} · {movement.reason}
                </p>
              </div>
              <div className="tabular shrink-0 text-right text-sm">
                {movement.amountUsd > 0 && <div>{formatUsd(movement.amountUsd)}</div>}
                {movement.amountLbp > 0 && (
                  <div className="text-xs text-[var(--text-secondary)]">
                    {formatLbp(movement.amountLbp)}
                  </div>
                )}
              </div>

              {/* Mirrors the server: a row owned by another record, or written before that
                  link existed, is undone through what owns it and never on its own. */}
              {movement.reversalOfId == null &&
                movement.referenceId == null &&
                movement.type !== 'ExpensePaid' &&
                movement.type !== 'AccountReceipt' && (
                <ReverseButton
                  isReversed={movement.reversedAtUtc != null}
                  label={`Reverse ${movement.type}`}
                  onClick={() => setReversingMovement(movement)}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      <ReverseDialog
        kind="cashMovement"
        id={reversingMovement?.id ?? null}
        what={
          reversingMovement
            ? `${reversingMovement.type} — ${formatUsd(reversingMovement.amountUsd)} · ${reversingMovement.reason}`
            : ''
        }
        onClose={() => setReversingMovement(null)}
        invalidate={['drawer', 'shift-expected', 'shift-statement', 'safe', 'safe-history']}
      />

      <Modal
        open={isOpen}
        onClose={() => setOpen(false)}
        title={t('Move cash')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button onClick={() => record.mutate()} disabled={record.isPending || !reason.trim()}>
              {t('Record')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Select label={t('What happened')} value={type} onChange={(e) => setType(e.target.value)}>
            <option value="PayIn">{t('Money put into the drawer')}</option>
            <option value="PayOut">{t('Money taken out')}</option>
            <option value="DropToSafe">{t('Moved to the safe')}</option>
            <option value="WithdrawFromSafe">{t('Brought from the safe for change')}</option>
            <option value="ExpensePaid">{t('An expense paid from the till')}</option>
            <option value="AccountReceipt">{t('A customer settling their account')}</option>
          </Select>
          <Input
            label={t('US dollars')}
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={usd}
            onChange={(e) => setUsd(e.target.value)}
            className="tabular"
            placeholder="0.00"
          />
          <Input
            label={t('Lebanese lira')}
            type="number"
            inputMode="numeric"
            min="0"
            value={lbp}
            onChange={(e) => setLbp(e.target.value)}
            className="tabular"
            placeholder="0"
          />
          <Input
            label={t('Reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('An unexplained movement is what a shortage looks like')}
            required
          />
          {error && (
            <p role="alert" className="rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
              {error}
            </p>
          )}
        </div>
      </Modal>
    </div>
  )
}

/* --------------------------------------------------------------------- safe */

/** Moving money in or out of the safe by hand, as opposed to a drop from a till. */
type SafeAction = 'OwnerDrawing' | 'OwnerInjection'

interface DrawerRow {
  id: number
  type: string
  amountUsd: number
  amountLbp: number
  reason: string
  occurredAtUtc: string
  reversedAtUtc: string | null
  reversalReason: string | null
  /** Set when this row is itself the contra that undid another. */
  reversalOfId: number | null
  /**
   * The record this movement is the cash side of. Set means it is owned by an expense, an
   * account payment or a sale, and can only be undone through that — so no button is offered.
   */
  referenceId: number | null
}

interface SafeRow {
  id: number
  type: string
  amountUsd: number
  amountLbp: number
  increasesSafe: boolean
  reason: string
  occurredAtUtc: string
  by: string | null
  isReversed: boolean
  reversalReason: string | null
  /** True when this row is itself the contra that undid another one. */
  isReversal: boolean
  reversedBy: string | null
  /**
   * The record this transaction is the safe side of — an expense, or a drop from a till. Set
   * means it can only be undone through that, so no button is offered.
   */
  referenceId: number | null
}

const SAFE_ACTIONS: Record<SafeAction, { title: string; verb: string; hint: string }> = {
  OwnerDrawing: {
    title: t('Take out profit'),
    verb: 'Take it out',
    // Said plainly, because the alternative — booking it as an expense — is the intuitive
    // mistake and it makes the shop look less profitable than it is.
    hint: 'Your share of what the shop has earned. It comes out of the safe and off no profit — taking money you have already made does not un-make it.',
  },
  OwnerInjection: {
    title: t('Put money in'),
    verb: 'Put it in',
    hint: 'Your own cash going into the business. Not income, so it does not count as a sale.',
  },
}

function SafeSection({ onDone }: { onDone: () => Promise<void> }) {
  const queryClient = useQueryClient()
  const ownBranchId = useSession((state) => state.session)?.branchId ?? null

  const [action, setAction] = useState<SafeAction | null>(null)
  const [branchId, setBranchId] = useState<number | ''>(ownBranchId ?? '')
  const [usd, setUsd] = useState('')
  const [lbp, setLbp] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  // An admin is not tied to a branch, so they have to say which safe they mean. A branch user
  // never sees this — the server pins them to their own regardless of what they send.
  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => apiClient.get<{ id: number; displayName: string }[]>('/api/branches'),
    enabled: ownBranchId === null,
  })

  const scoped = ownBranchId ?? (branchId === '' ? null : Number(branchId))

  const balance = useQuery({
    queryKey: ['safe', scoped],
    queryFn: () =>
      apiClient.get<{ usd: number; lbp: number }>(
        scoped === null ? '/api/cash/safe' : `/api/cash/safe?branchId=${scoped}`,
      ),
  })

  const [safeFilters, setSafeFilters] = useState<ListFilterState>(NO_FILTERS)

  const history = usePagedList<SafeRow>(['safe-history'], '/api/cash/safe/history', {
    from: safeFilters.from,
    to: safeFilters.to,
  })

  const record = useMutation({
    mutationFn: () =>
      apiClient.post('/api/cash/safe', {
        branchId: scoped,
        type: action,
        amountUsd: Number(usd || 0),
        amountLbp: Number(lbp || 0),
        reason: reason.trim(),
        reference: null,
        isIncrease: null,
      }),
    onSuccess: async () => {
      setAction(null)
      setUsd('')
      setLbp('')
      setReason('')
      await queryClient.invalidateQueries({ queryKey: ['safe'] })
      await queryClient.invalidateQueries({ queryKey: ['safe-history'] })
      await onDone()
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not record that.'),
  })

  const chosen = action ? SAFE_ACTIONS[action] : null
  const held = balance.data

  function open(next: SafeAction) {
    setAction(next)
    setUsd('')
    setLbp('')
    setReason('')
    setError(null)
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        <Button onClick={() => open('OwnerDrawing')}>
          <HandCoins className="h-4 w-4" />
          {t('Take out profit')}
        </Button>
        <Button variant="secondary" onClick={() => open('OwnerInjection')}>
          <ArrowDownToLine className="h-4 w-4" />
          {t('Put money in')}
        </Button>
      </div>

      {/* The safe's balance is summed over all time, so this list only ever grows. Dated rather
          than searched: the question is always about a particular day's count. */}
      <ListFilters value={safeFilters} onChange={setSafeFilters} searchHint="" showSearch={false} />

      {history.page.length === 0 ? (
        <EmptyState
          icon={Landmark}
          title={t('The safe is empty')}
          description={t('Drops from a till and money you put in yourself appear here.')}
        />
      ) : (
        <>
          <SafeHistory entries={history.page} />

          <Pager list={history} />
        </>
      )}

      <Modal
        open={action !== null}
        onClose={() => setAction(null)}
        title={chosen?.title ?? ''}
        footer={
          <>
            <Button variant="secondary" onClick={() => setAction(null)}>
              {t('Cancel')}
            </Button>
            <Button
              onClick={() => record.mutate()}
              disabled={
                record.isPending
                || !reason.trim()
                || scoped === null
                || (Number(usd || 0) <= 0 && Number(lbp || 0) <= 0)
              }
            >
              {record.isPending ? 'Recording…' : (chosen?.verb ?? 'Save')}
            </Button>
          </>
        }
      >
        <p className="mb-4 text-sm text-[var(--text-secondary)]">{chosen?.hint}</p>

        {ownBranchId === null && (
          <Select
            label={t('Which safe')}
            value={branchId}
            wrapperClassName="mb-4"
            onChange={(e) => setBranchId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">{t('Choose a branch…')}</option>
            {branches.data?.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.displayName}
              </option>
            ))}
          </Select>
        )}

        {/* The hard limit on anything going out: there is either cash in the safe or there is not.
            Scoped to the chosen branch, since that is what the server checks against. */}
        {held && action !== 'OwnerInjection' && scoped !== null && (
          <p className="surface-sunken tabular mb-4 rounded-lg px-3 py-2 text-sm">
            This safe holds {formatUsd(held.usd)} and {formatLbp(held.lbp)}
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t('US dollars')}
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={usd}
            onChange={(e) => setUsd(e.target.value)}
            className="tabular"
            placeholder="0.00"
            autoFocus
          />
          <Input
            label={t('Lebanese lira')}
            type="number"
            inputMode="numeric"
            min="0"
            value={lbp}
            onChange={(e) => setLbp(e.target.value)}
            className="tabular"
            placeholder="0"
          />
        </div>

        <Input
          label={t('What is it for')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          wrapperClassName="mt-4"
          placeholder={action === 'OwnerDrawing' ? 'August drawings' : 'Reason'}
          required
        />

        {error && (
          <p role="alert" className="mt-3 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
            <TriangleAlert className="mr-1 inline h-4 w-4" />
            {error}
          </p>
        )}
      </Modal>
    </div>
  )
}

function SafeHistory({ entries }: { entries: SafeRow[] }) {
  /** The transaction being undone. */
  const [reversing, setReversing] = useState<SafeRow | null>(null)

  return (
    <>
      <ul className="flex flex-col gap-2">
        {entries.map((entry) => (
          <li
            key={entry.id}
            className="surface-raised flex items-center gap-3 rounded-[var(--radius-card)] border p-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`text-sm font-medium ${
                    entry.isReversed ? 'text-[var(--text-secondary)] line-through' : ''
                  }`}
                >
                  {entry.type}
                </span>
                <Badge tone={entry.increasesSafe ? 'success' : 'neutral'}>
                  {entry.increasesSafe ? 'in' : 'out'}
                </Badge>
                {entry.isReversed && (
                  <ReversedBadge reason={entry.reversalReason} by={entry.reversedBy} />
                )}
                {/* Both halves stay in the balance — the pair is what nets to nothing — so the
                    contra is labelled rather than hidden, or it reads as a second transaction. */}
                {entry.isReversal && <Badge tone="accent">{t('Reversal')}</Badge>}
              </div>
              <p className="truncate text-xs text-[var(--text-secondary)]">
                {formatMoment(entry.occurredAtUtc)} · {entry.reason}
                {entry.by ? ` · ${entry.by}` : ''}
              </p>
            </div>
            <div className="tabular shrink-0 text-right text-sm">
              {entry.amountUsd > 0 && <div>{formatUsd(entry.amountUsd)}</div>}
              {entry.amountLbp > 0 && (
                <div className="text-xs text-[var(--text-secondary)]">{formatLbp(entry.amountLbp)}</div>
              )}
            </div>

            {!entry.isReversal && entry.referenceId == null && (
              <ReverseButton
                isReversed={entry.isReversed}
                label={`Reverse ${entry.type}`}
                onClick={() => setReversing(entry)}
              />
            )}
          </li>
        ))}
      </ul>

      <ReverseDialog
        kind="safeTransaction"
        id={reversing?.id ?? null}
        what={reversing ? `${reversing.type} — ${formatUsd(reversing.amountUsd)} · ${reversing.reason}` : ''}
        onClose={() => setReversing(null)}
        invalidate={['safe', 'safe-history', 'reports', 'shift-expected', 'shift-statement']}
      />
    </>
  )
}

/* -------------------------------------------------------------------- close */

interface CloseResult {
  varianceUsd: number
  varianceLbp: number
  varianceTotalUsd: number
  balancesOverall: boolean
}

function CloseSection({
  shiftId,
  expected,
  statement,
  rate,
  onDone,
  onClosed,
}: {
  shiftId: number
  expected: ExpectedCash | undefined
  statement: DrawerStatementData | undefined
  rate: number
  onDone: () => Promise<void>
  onClosed: (result: CloseResult) => void
}) {
  const queryClient = useQueryClient()
  const [usd, setUsd] = useState('')
  const [lbp, setLbp] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  const close = useMutation({
    mutationFn: () =>
      apiClient.post<CloseResult>(`/api/shifts/${shiftId}/close`, {
        // The same reading the preview above is built from, so what the cashier signed off is
        // what gets recorded.
        countedCashUsd: amount(usd),
        countedCashLbp: amount(lbp),
        notes: notes.trim() || null,
      }),
    onSuccess: async (data) => {
      // Handed up before the invalidation: refreshing the shift query is what tears this
      // section down, so the result has to be somewhere safe by then.
      onClosed(data)
      await queryClient.invalidateQueries({ queryKey: ['shift'] })
      await onDone()
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not close the shift.'),
  })

  // A drawer that is short or over needs a reason attached while the shift still exists.
  // Currency swapped between the two piles nets to nothing and is not a difference — asking for
  // a note on an ordinary bit of change-making would only teach cashiers to type anything.
  const needsExplanation =
    expected !== undefined &&
    !variance(amount(usd), amount(lbp), expected, rate).balances &&
    notes.trim() === ''

  return (
    <div>
      {/* Before the trading summary, because this is the part the count is checked against —
          the cashier is about to be asked for a figure and needs to see where it came from. */}
      {statement && (
        <div className="mb-4">
          <DrawerStatement statement={statement} title={t('Z-report — how the drawer got here')} />
        </div>
      )}


      <div className="surface-raised rounded-[var(--radius-card)] border p-4">
        <p className="mb-1 text-sm font-medium">{t('Count the drawer')}</p>
        <p className="mb-4 text-xs text-[var(--text-secondary)]">
          {t('Count first, then enter it. The expected figure is deliberately not pre-filled — a count that agrees because it was copied proves nothing.')}
        </p>

        <div className="flex flex-col gap-4">
          <Input
            label={t('Counted US dollars')}
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={usd}
            onChange={(e) => setUsd(e.target.value)}
            className="tabular text-lg"
            placeholder="0.00"
          />
          <Input
            label={t('Counted Lebanese lira')}
            type="number"
            inputMode="numeric"
            min="0"
            value={lbp}
            onChange={(e) => setLbp(e.target.value)}
            className="tabular text-lg"
            placeholder="0"
            hint={t('Enter 0 if there is no lira in the drawer — both piles have to be counted')}
          />
          <Input
            label={needsExplanation ? 'Notes — required' : 'Notes'}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('Anything that explains a difference')}
            hint={
              needsExplanation
                ? 'The drawer does not balance. Say what happened — the shift cannot be reopened to explain it later.'
                : undefined
            }
          />
        </div>

        {/*
          Both views are shown together and neither replaces the other. The per-currency lines
          say which pile moved; the net says whether any money is actually missing. A cashier
          who gives change out of the other currency is short in one and over in the other by
          the same value — two alarming-looking figures for a drawer that is perfectly correct.

          Both legs are read exactly as they will be submitted, an empty box included. An
          earlier version substituted the *expected* figure for an empty one, which rendered the
          untouched currency as balanced while the request still sent zero — so a cashier could
          count only the dollars, be shown "Balanced", and have the whole lira float recorded as
          missing. Shown from the moment the expected figures load, so neither pile can be
          overlooked for want of appearing on screen.
        */}
        {expected && (
          <CountSummary
            countedUsd={amount(usd)}
            countedLbp={amount(lbp)}
            expected={expected}
            rate={rate}
          />
        )}

        {error && (
          <p role="alert" className="mt-4 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
            {error}
          </p>
        )}

        {/*
          Both boxes, not just the dollars. A blank lira box is not a count of zero lira — it is
          a pile nobody counted, and the close is the one moment that can never be revisited.
          Typing 0 is a cashier saying there was no lira; leaving it empty says nothing at all.

          A difference additionally needs a note. The server refuses one without it, so this only
          keeps the button from offering what would be turned away.
        */}
        <Button
          size="lg"
          full
          className="mt-4"
          onClick={() => close.mutate()}
          disabled={close.isPending || usd === '' || lbp === '' || needsExplanation}
        >
          <Lock className="h-5 w-5" />
          {close.isPending ? 'Closing…' : 'Close the shift'}
        </Button>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ helpers */

/**
 * A typed count as a number, reading exactly the way the request body does — an empty or
 * unparseable box is zero, never a stand-in for what was expected. The preview and the close
 * must never be able to disagree about what was counted.
 */
function amount(value: string): number {
  const parsed = Number(value)
  return value === '' || Number.isNaN(parsed) ? 0 : parsed
}

/**
 * How far a count is from what the drawer should hold — each currency, and the two combined at
 * the current rate.
 *
 * Computed in one place so the summary on screen and the rule deciding whether the shift may be
 * closed are answering the same question. The server applies the same test in
 * `ShiftService.CloseAsync`; this only avoids offering a button that would be refused.
 */
function variance(countedUsd: number, countedLbp: number, expected: ExpectedCash, rate: number) {
  const usdDiff = countedUsd - expected.expectedUsd
  const lbpDiff = countedLbp - expected.expectedLbp
  // The lira leg is valued at the current rate to put both on one scale.
  const netUsd = rate > 0 ? usdDiff + lbpDiff / rate : usdDiff

  return { usdDiff, lbpDiff, netUsd, balances: Math.abs(netUsd) < 0.01 }
}

function CountSummary({
  countedUsd,
  countedLbp,
  expected,
  rate,
}: {
  countedUsd: number
  countedLbp: number
  expected: ExpectedCash
  rate: number
}) {
  const { usdDiff, lbpDiff, netUsd, balances } = variance(countedUsd, countedLbp, expected, rate)
  const swapped = balances && (Math.abs(usdDiff) >= 0.01 || Math.abs(lbpDiff) >= 1)

  return (
    <dl className="surface-sunken mt-3 flex flex-col gap-1 rounded-lg p-3 text-sm">
      <VarianceRow label={t('US dollars')} difference={usdDiff} format={formatUsd} />
      <VarianceRow label={t('Lebanese lira')} difference={lbpDiff} format={formatLbp} unit={1} />

      <div className="mt-1 flex justify-between border-t border-[var(--border-subtle)] pt-2">
        <dt className="font-medium">{t('Net position')}</dt>
        <dd
          className={`tabular font-semibold ${
            balances ? 'text-success-500' : netUsd > 0 ? 'text-warn-500' : 'text-danger-500'
          }`}
        >
          {balances
            ? 'Balanced'
            : `${formatUsd(Math.abs(netUsd))} ${netUsd > 0 ? 'over' : 'short'}`}
        </dd>
      </div>

      {swapped && (
        <p className="mt-1 text-xs text-[var(--text-secondary)]">
          {t('The two cancel out at {rate} to the dollar — change was given from the other currency.', { rate: rate.toLocaleString() })}
        </p>
      )}
    </dl>
  )
}

/** One currency's difference between what was counted and what was expected. */
function VarianceRow({
  label,
  difference,
  format,
  unit = 0.01,
}: {
  label: string
  difference: number
  format: (value: number) => string
  /** Smallest amount worth reporting: a cent for dollars, a whole lira for LBP. */
  unit?: number
}) {
  // Rounded to that unit first, so a fraction below it never reads as a discrepancy.
  const rounded = Math.round(difference / unit) * unit
  const tone =
    rounded === 0 ? 'text-success-500' : rounded > 0 ? 'text-warn-500' : 'text-danger-500'

  return (
    <div className="flex justify-between">
      <dt className="text-[var(--text-secondary)]">{label}</dt>
      <dd className={`tabular font-medium ${tone}`}>
        {rounded === 0
          ? 'exact'
          : `${format(Math.abs(rounded))} ${rounded > 0 ? 'over' : 'short'}`}
      </dd>
    </div>
  )
}
